<script>
import { mapGetters } from "vuex";
import boxService from "@/services/box";

import leave from "@/mixins/leave";
import institutionService from "@/services/institution";
import Spinner from "@/components/ui/Spinner";
import InputTag from "@/components/ui/InputTag";

export default {
  name: "ModalInstitution",
  mixins: [leave],

  components: {
    Spinner,
    InputTag,
  },

  props: {
    showDialogModal: {
      type: Boolean,
    },
    currentInstitution: {
      type: Object,
    },
    mode: {
      type: String,
      default: "institution",
    },
  },

  data() {
    return {
      isLoading: false,
      isPosting: false,
      institutionId: null,
      institution: {
        address: {
          betweenStreets: ["", ""],
        },
      },
      isFetchingInstitution: false,
      isUpdate: !!this.currentInstitution?._id,
      isShowed: this.showDialogModal,
      boxes: [],
      newBox: {
        name: "",
      },
    };
  },

  computed: {
    ...mapGetters(["isAdmin", "isDoctor"]),
  },

  created() {
    if (this.isUpdate) {
      this.institution = this.currentInstitution;
      if (this.mode === "boxes") {
        this.fetchBoxes();
      }
    }
  },

  mounted() {
    document.addEventListener("keyup", this.escape);
  },

  beforeDestroy() {
    document.removeEventListener("keyup", this.escape);
  },

  methods: {
    escape(event) {
      if (event.keyCode == 27) this.close();
    },

    submit() {
      if (this.mode !== "institution") return;
      if (this.isUpdate) {
        this.updateInstitution();
      } else {
        this.createInstitution();
      }
    },

    createInstitution() {
      this.isPosting = true;

      institutionService
        .createInstitution(this.institution)
        .then((institution) => {
          this.institutionId = institution._id;
          this.$bus.$emit("institution-created", institution);
          this.close();
        })
        .catch(() => {
          this.isPosting = false;
        });
    },

    updateInstitution() {
      this.isPosting = true;

      institutionService
        .updateInstitution(this.institution)
        .then((institution) => {
          this.institutionId = institution._id;
          this.$bus.$emit("institution-updated", institution);
          this.close();
        })
        .catch(() => {
          this.isPosting = false;
        });
    },

    close() {
      this.isPosting = false;
      this.isShowed = false;
      this.$emit("close");
    },
    async fetchBoxes() {
      if (!this.institution._id) return;
      try {
        this.boxes = await boxService.getBoxes(this.institution._id);
      } catch (e) {
        console.error("Error fetching boxes", e);
      }
    },

    async createBox() {
      if (!this.newBox.name.trim()) return;
      try {
        const data = await boxService.createBox({
          name: this.newBox.name.trim(),
          institution: this.institution._id,
        });
        this.boxes.push(data);
        this.newBox.name = "";
      } catch (e) {
        console.error("Error creating box", e);
      }
    },

    async deleteBox(id) {
      try {
        await boxService.deleteBox(id);
        this.boxes = this.boxes.filter((o) => o._id !== id);
      } catch (e) {
        console.error("Error deleting box", e);
      }
    },
  },
};
</script>

<template lang="pug">
ValidationObserver(v-slot="{handleSubmit}")
  form.modal(@submit.prevent="handleSubmit(submit)")
    header.modal__header
      h2.modal__title(v-if="mode === 'institution'") Institución
      h2.modal__title(v-else) Boxes de {{ institution.name }}
      .modal__actions
        el-button(type="info" @click="close()") Cancelar
        el-button.border(
          v-if="mode === 'institution'"
          type="primary"
          native-type="submit"
          :loading="isPosting"
        ) Guardar
    p.modal__subtitle(v-if="mode === 'institution'") Los campos con (*) son obligatorios
    p.modal__subtitle(v-else) Gestión de boxes de la institución
    .modal__content(v-if="isFetchingInstitution")
      spinner
    .modal__content(v-else)
      .modal__block(v-if="mode === 'institution'")
        .modal__section
          .modal__sign.sign
            .sign__icon
              micon(name="assignment")
            h3.sign__title Detalle
          article.modal__fields
            .modal__row
              fieldset.modal__field
                label.label * Nombre
                ValidationProvider(name="Name" rules="required" v-slot="{ errors }")
                  el-input(v-model="institution.name")
                  span.has-error {{errors[0]}}
              fieldset.modal__field
                ValidationProvider(name="Abbreviation" rules="required" v-slot="{ errors }")
                  label.label * Abreviación
                  el-input(v-model="institution.abbreviation")
                  span.has-error {{errors[0]}}
              fieldset.modal__field
                label.label ID externo
                el-input(v-model="institution.publicId")
            .modal__row
                fieldset.modal__field
                    label.label Email
                    ValidationProvider(name="Email", rules="email", v-slot="{ errors }")
                      el-input(type="email", v-model="institution.email")
                      span.has-error {{ errors[0] }}
                fieldset.modal__field
                  label.label Teléfono
                  el-input(v-model="institution.phone", name="Teléfono")  
      .modal__block(v-if="mode === 'institution'")
        .modal__section
          .modal__sign.sign
            .sign__icon
                micon(name="location_on")
            h3.sign__title Dirección
          article.modal__fields.address
                .modal__row
                  fieldset.modal__field
                    label.label * Calle
                    ValidationProvider(name="Calle" rules="required" v-slot="{ errors }")
                      el-input(v-model="institution.address.street")
                      span.has-error {{errors[0]}}
                  fieldset.modal__field  
                    label.label * Número
                    ValidationProvider(name="Numero" rules="required" v-slot="{ errors }")
                      el-input(v-model="institution.address.buildingNumber")
                      span.has-error {{errors[0]}}
                  fieldset.modal__field  
                    label.label Piso
                    el-input(v-model="institution.address.apartmentFloor")
                .modal__row
                  fieldset.modal__field  
                    label.label Número de puerta
                    el-input(v-model="institution.address.apartmentNumber")
                  fieldset.modal__field  
                    label.label * Código postal
                    ValidationProvider(name="Código postal" rules="required" v-slot="{ errors }")
                      el-input(v-model="institution.address.postalCode")
                      span.has-error {{errors[0]}}
                .modal__row
                  fieldset.modal__field  
                    label.label * Ciudad
                    ValidationProvider(name="Ciudad" rules="required" v-slot="{ errors }")
                      el-input(v-model="institution.address.city")
                      span.has-error {{errors[0]}}
                  fieldset.modal__field  
                    label.label * Distrito
                    ValidationProvider(name="Distrito" rules="required" v-slot="{ errors }")
                      el-input(v-model="institution.address.district")
                      span.has-error {{errors[0]}}
                  fieldset.modal__field  
                    label.label Barrio
                    el-input(v-model="institution.address.neighborhood")
                .modal__row
                  fieldset.modal__field  
                    label.label Entre Calle 1
                    el-input(v-model="institution.address.betweenStreets[0]")
                  fieldset.modal__field  
                    label.label Entre Calle 2
                    el-input(v-model="institution.address.betweenStreets[1]")      
                .modal__row
                  fieldset.modal__field  
                    label.label Latitud
                    el-input(v-model="institution.address.lat" type="number" step="any")
                  fieldset.modal__field
                    label.label Longitud
                    el-input(v-model="institution.address.lng" type="number" step="any")      


      .modal__block(v-if="mode === 'boxes'")
        .modal__section
          .modal__sign.sign
            .sign__icon
              micon(name="edit")
            h3.sign__title Boxes
          article.modal__fields
            .modal__row.full
              el-input(
                v-model="newBox.name",
                placeholder="Nombre de nuevo box"
              )
              el-button(type="primary", @click="createBox", :disabled="!newBox.name") Agregar
            table.box-list
              tbody
              tr(v-for="box in boxes" :key="box._id")
                td {{ box.name }}
                td.actions
                  el-button(
                    type="danger",
                    size="mini",
                    icon="el-icon-delete",
                    @click="deleteBox(box._id)"
                  )
</template>

<style lang="scss" scoped>
.location {
  .micon {
    margin-right: 10px;
  }
}

.modal {
  z-index: 9999;
}

.box-list {
  width: 100%;
  margin-top: 10px;
  border-collapse: collapse;

  th {
    text-align: left;
    padding: 8px;
    background: #f7f7f7;
    font-weight: 600;
    border-bottom: 1px solid #ddd;
  }
  tr {
    border-bottom: 1px solid #eaeaea;
  }

  td {
    padding: 6px 8px;
    &:first-child {
      width: 90%;
    }
    &:last-child {
      text-align: right;
    }
  }
}

.event-detail-modal {
  h2 {
    font-weight: bold;
    margin-bottom: 20px;
  }

  h3 {
    margin: 0 0 20px;
  }

  .form-item {
    display: flex;
    flex-direction: column;
    margin-bottom: 30px;

    .description {
      word-break: break-word;
      font-weight: bold;
      color: $primary;
      margin-bottom: 10px;
    }

    .response {
      margin-left: 20px;
      display: list-item;
      list-style-type: "→ ";
    }
  }

  .view-task {
    padding: 1rem;
  }
  tr {
    border-bottom: 1px solid #eee;
  }

  td {
    padding: 8px;
  }

  td.actions {
    text-align: right;
    width: 100px;
  }
}

.modal__row.full {
  display: flex;
  align-items: center;
  gap: 10px;

  .el-input {
    flex: 1;
  }
}
@media (min-width: 446px) {
  .container {
    flex-direction: row !important;
    justify-content: space-between;
  }

  .betweenStreet {
    width: 48.5% !important;

    &:last-child {
      margin-top: 0 !important;
    }
  }
}
</style>
